import React, { useState, useEffect, useCallback, useMemo } from "react";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import {
  Building2, Plus, Edit, Users, GitBranch, Loader2, Save,
  CheckCircle, XCircle, Archive, Globe, ChevronRight, Trash2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { HelpBubble } from "../components/HelpBubble";

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-700",
  suspended: "bg-amber-100 text-amber-700",
  archived: "bg-slate-100 text-slate-500",
};

const TYPE_COLORS = {
  holding: "bg-purple-100 text-purple-700",
  subsidiary: "bg-blue-100 text-blue-700",
  branch: "bg-cyan-100 text-cyan-700",
  independent: "bg-slate-100 text-slate-600",
};

const PLAN_COLORS = {
  starter: "bg-slate-50 text-slate-600 border-slate-200",
  professional: "bg-cyan-50 text-cyan-700 border-cyan-200",
  enterprise: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function PlatformAdminPage() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [groups, setGroups] = useState([]);
  const [members, setMembers] = useState([]);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [editMembership, setEditMembership] = useState(null); // { user, org_id }
  const [addMembershipOpen, setAddMembershipOpen] = useState(false);
  const [filterOrgId, setFilterOrgId] = useState("all");

  const fetchAll = useCallback(async () => {
    try {
      const [orgsRes, groupsRes, statusRes, membersRes] = await Promise.all([
        apiClient.get("/platform/organizations"),
        apiClient.get("/platform/groups"),
        apiClient.get("/platform/migration-status"),
        apiClient.get("/platform/members"),
      ]);
      setOrgs(orgsRes.data);
      setGroups(groupsRes.data);
      setMigrationStatus(statusRes.data);
      setMembers(membersRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load platform data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const activeOrgs = useMemo(() => orgs.filter(o => o.status === "active"), [orgs]);
  const totalUsers = useMemo(() => orgs.reduce((sum, o) => sum + (o.active_users || 0), 0), [orgs]);

  if (user?.role !== "superuser") {
    return (
      <div className="text-center py-20">
        <Building2 size={48} className="mx-auto mb-4 text-slate-300" />
        <p className="text-slate-500">Platform administration requires superuser access.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="animate-spin text-slate-400" size={32} /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="platform-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Platform Administration <HelpBubble tip="Manage tenant organizations, holding groups, and user memberships across the entire platform." />
          </h1>
          <p className="text-slate-500 text-sm mt-1">Multi-tenant organization management</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard icon={Building2} label="Organizations" value={orgs.length} sub={`${activeOrgs.length} active`} color="bg-purple-100 text-purple-600" />
        <StatCard icon={Users} label="Platform Users" value={totalUsers} sub="across all tenants" color="bg-blue-100 text-blue-600" />
        <StatCard icon={GitBranch} label="Groups" value={groups.length} sub="holding structures" color="bg-cyan-100 text-cyan-600" />
        <StatCard icon={Globe} label="Databases" value={migrationStatus?.organizations || 0} sub="tenant DBs provisioned" color="bg-emerald-100 text-emerald-600" />
      </div>

      <Tabs defaultValue="organizations" className="w-full">
        <TabsList>
          <TabsTrigger value="organizations" data-testid="orgs-tab">Organizations ({orgs.length})</TabsTrigger>
          <TabsTrigger value="members" data-testid="members-tab">Members ({members.length})</TabsTrigger>
          <TabsTrigger value="groups" data-testid="groups-tab">Groups ({groups.length})</TabsTrigger>
        </TabsList>

        {/* Organizations Tab */}
        <TabsContent value="organizations">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">Tenant Organizations</CardTitle>
              <Button size="sm" className="gap-2 rounded-sm" onClick={() => setCreateOrgOpen(true)} data-testid="create-org-btn">
                <Plus size={14} /> New Organization
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="orgs-table">
                  <thead>
                    <tr className="border-b text-left text-xs font-mono uppercase tracking-wider text-slate-400">
                      <th className="pb-3 pr-4">Organization</th>
                      <th className="pb-3 pr-4">Type</th>
                      <th className="pb-3 pr-4">Plan</th>
                      <th className="pb-3 pr-4">Users</th>
                      <th className="pb-3 pr-4">Max</th>
                      <th className="pb-3 pr-4">Status</th>
                      <th className="pb-3 pr-4">Created</th>
                      <th className="pb-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgs.map((org) => (
                      <tr key={org.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`org-row-${org.id}`}>
                        <td className="py-3 pr-4">
                          <div>
                            <p className="font-medium text-slate-900">{org.name}</p>
                            <p className="text-xs text-slate-400 font-mono">{org.slug}</p>
                          </div>
                        </td>
                        <td className="py-3 pr-4">
                          <Badge className={`rounded-sm text-[10px] ${TYPE_COLORS[org.type] || TYPE_COLORS.independent}`}>{org.type}</Badge>
                        </td>
                        <td className="py-3 pr-4">
                          <Badge className={`rounded-sm text-[10px] border ${PLAN_COLORS[org.plan] || PLAN_COLORS.starter}`}>{org.plan}</Badge>
                        </td>
                        <td className="py-3 pr-4 font-mono">{org.active_users || 0}</td>
                        <td className="py-3 pr-4 font-mono text-slate-400">{org.max_users}</td>
                        <td className="py-3 pr-4">
                          <Badge className={`rounded-sm text-[10px] ${STATUS_COLORS[org.status] || STATUS_COLORS.active}`}>{org.status}</Badge>
                        </td>
                        <td className="py-3 pr-4 text-xs text-slate-400">{org.created_at ? new Date(org.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "-"}</td>
                        <td className="py-3">
                          <Button variant="outline" size="sm" className="h-7 rounded-sm gap-1 text-xs" onClick={() => setEditOrg(org)} data-testid={`edit-org-${org.id}`}>
                            <Edit size={12} /> Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {orgs.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Building2 size={32} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No organizations yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Members Tab */}
        <TabsContent value="members">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <div className="flex items-center gap-4">
                <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">Platform Members</CardTitle>
                <Select value={filterOrgId} onValueChange={setFilterOrgId}>
                  <SelectTrigger className="rounded-sm h-8 w-[220px] text-xs" data-testid="member-org-filter">
                    <SelectValue placeholder="Filter by org" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Organizations</SelectItem>
                    {activeOrgs.map(o => (
                      <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" className="gap-2 rounded-sm" onClick={() => setAddMembershipOpen(true)} data-testid="add-membership-btn">
                <Plus size={14} /> Add Membership
              </Button>
            </CardHeader>
            <CardContent>
              <MembersTable
                members={members}
                filterOrgId={filterOrgId}
                onEditMembership={(user, orgId) => setEditMembership({ user, org_id: orgId })}
                onRemoveMembership={async (userId, orgId) => {
                  if (!window.confirm("Remove this user from the organization?")) return;
                  try {
                    await apiClient.delete(`/platform/users/${userId}/memberships/${orgId}`);
                    toast.success("Membership removed");
                    fetchAll();
                  } catch (err) { toast.error(err.response?.data?.detail || "Failed to remove"); }
                }}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Groups Tab */}
        <TabsContent value="groups">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">Holding Company Groups</CardTitle>
              <Button size="sm" className="gap-2 rounded-sm" onClick={() => setCreateGroupOpen(true)} data-testid="create-group-btn">
                <Plus size={14} /> New Group
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {groups.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <GitBranch size={32} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No groups yet. Create a group to link holding companies with subsidiaries.</p>
                </div>
              ) : (
                groups.map((group) => {
                  const memberOrgs = orgs.filter(o => group.member_org_ids?.includes(o.id));
                  const holdingOrg = orgs.find(o => o.id === group.holding_org_id);
                  return (
                    <div key={group.id} className="border rounded-sm p-4" data-testid={`group-${group.id}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 rounded bg-purple-100 flex items-center justify-center">
                          <GitBranch size={16} className="text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium">{group.name}</p>
                          <p className="text-xs text-slate-400">Holding: {holdingOrg?.name || group.holding_org_id}</p>
                        </div>
                        <Badge className="ml-auto bg-slate-100 text-slate-600 rounded-sm text-xs">{memberOrgs.length} entities</Badge>
                      </div>
                      <div className="space-y-1 pl-12">
                        {memberOrgs.map((org) => (
                          <div key={org.id} className="flex items-center gap-2 text-sm text-slate-600">
                            <ChevronRight size={12} className="text-slate-300" />
                            <span>{org.name}</span>
                            <Badge className={`rounded-sm text-[9px] ${TYPE_COLORS[org.type]}`}>{org.type}</Badge>
                            <Badge className={`rounded-sm text-[9px] ${STATUS_COLORS[org.status]}`}>{org.status}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <CreateOrgDialog open={createOrgOpen} onOpenChange={setCreateOrgOpen} orgs={orgs} onCreated={fetchAll} />
      <EditOrgDialog org={editOrg} onOpenChange={() => setEditOrg(null)} onUpdated={fetchAll} />
      <CreateGroupDialog open={createGroupOpen} onOpenChange={setCreateGroupOpen} orgs={orgs} onCreated={fetchAll} />
      <EditMembershipDialog membership={editMembership} onOpenChange={() => setEditMembership(null)} onUpdated={fetchAll} />
      <AddMembershipDialog open={addMembershipOpen} onOpenChange={setAddMembershipOpen} members={members} orgs={orgs} onAdded={fetchAll} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded flex items-center justify-center ${color}`}>
            <Icon size={18} />
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-slate-500">{label}</p>
            {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CreateOrgDialog({ open, onOpenChange, orgs, onCreated }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", slug: "", type: "independent", plan: "starter", max_users: 15,
    parent_org_id: "", admin_contact_email: "", admin_contact_name: "",
  });

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error("Organization name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, max_users: parseInt(form.max_users) || 15 };
      if (!payload.slug) payload.slug = payload.name;
      if (!payload.parent_org_id) delete payload.parent_org_id;
      await apiClient.post("/platform/organizations", payload);
      toast.success(`Organization "${form.name}" created with tenant database`);
      onOpenChange(false);
      setForm({ name: "", slug: "", type: "independent", plan: "starter", max_users: 15, parent_org_id: "", admin_contact_email: "", admin_contact_name: "" });
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create organization");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Building2 size={18} className="text-purple-600" /> Create Organization</DialogTitle>
          <DialogDescription>Provision a new tenant with its own isolated database.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Acme Corp" className="rounded-sm" data-testid="create-org-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Slug</Label>
              <Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="Auto-generated from name" className="rounded-sm font-mono text-xs" data-testid="create-org-slug" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Type</Label>
              <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                <SelectTrigger className="rounded-sm" data-testid="create-org-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="independent">Independent</SelectItem>
                  <SelectItem value="holding">Holding Company</SelectItem>
                  <SelectItem value="subsidiary">Subsidiary</SelectItem>
                  <SelectItem value="branch">Branch</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Plan</Label>
              <Select value={form.plan} onValueChange={v => setForm({ ...form, plan: v })}>
                <SelectTrigger className="rounded-sm" data-testid="create-org-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter (15 users)</SelectItem>
                  <SelectItem value="professional">Professional (50 users)</SelectItem>
                  <SelectItem value="enterprise">Enterprise (999 users)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Max Users</Label>
              <Input type="number" value={form.max_users} onChange={e => setForm({ ...form, max_users: e.target.value })} className="rounded-sm" data-testid="create-org-max-users" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Parent Organization</Label>
              <Select value={form.parent_org_id || "none"} onValueChange={v => setForm({ ...form, parent_org_id: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-sm" data-testid="create-org-parent"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (top-level)</SelectItem>
                  {orgs.filter(o => o.status === "active").map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Admin Contact Name</Label>
              <Input value={form.admin_contact_name} onChange={e => setForm({ ...form, admin_contact_name: e.target.value })} placeholder="John Doe" className="rounded-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Admin Contact Email</Label>
              <Input type="email" value={form.admin_contact_email} onChange={e => setForm({ ...form, admin_contact_email: e.target.value })} placeholder="admin@acme.com" className="rounded-sm" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={handleCreate} disabled={saving} className="rounded-sm gap-2" data-testid="confirm-create-org-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Create & Provision
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditOrgDialog({ org, onOpenChange, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (org) {
      setForm({
        name: org.name || "",
        status: org.status || "active",
        plan: org.plan || "starter",
        max_users: org.max_users || 15,
        admin_contact_email: org.admin_contact_email || "",
        admin_contact_name: org.admin_contact_name || "",
      });
    }
  }, [org]);

  const handleUpdate = async () => {
    if (!org) return;
    setSaving(true);
    try {
      const payload = { ...form, max_users: parseInt(form.max_users) || 15 };
      await apiClient.put(`/platform/organizations/${org.id}`, payload);
      toast.success(`Organization "${form.name}" updated`);
      onOpenChange();
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!org} onOpenChange={() => onOpenChange()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Edit size={18} className="text-blue-600" /> Edit Organization</DialogTitle>
          <DialogDescription>Update tenant configuration for {org?.name}</DialogDescription>
        </DialogHeader>
        {org && (
          <div className="space-y-3 py-2">
            <div className="text-xs text-slate-400 font-mono bg-slate-50 rounded-sm p-2" data-testid="edit-org-id">
              ID: {org.id} | DB: {org.db_name} | Slug: {org.slug}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="edit-org-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="edit-org-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Plan</Label>
                <Select value={form.plan} onValueChange={v => setForm({ ...form, plan: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="edit-org-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starter">Starter (15 users)</SelectItem>
                    <SelectItem value="professional">Professional (50 users)</SelectItem>
                    <SelectItem value="enterprise">Enterprise (999 users)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Max Users</Label>
                <Input type="number" value={form.max_users} onChange={e => setForm({ ...form, max_users: e.target.value })} className="rounded-sm" data-testid="edit-org-max-users" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Admin Contact Name</Label>
                <Input value={form.admin_contact_name} onChange={e => setForm({ ...form, admin_contact_name: e.target.value })} className="rounded-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Admin Contact Email</Label>
                <Input type="email" value={form.admin_contact_email} onChange={e => setForm({ ...form, admin_contact_email: e.target.value })} className="rounded-sm" />
              </div>
            </div>

            {/* E-Signature plan info */}
            <div className="border-t pt-3 mt-2">
              <p className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-1">E-Signature Eligibility</p>
              {["professional", "enterprise"].includes(form.plan) ? (
                <div className="flex items-center gap-2 text-xs text-emerald-600">
                  <CheckCircle size={12} /> E-signature provider configuration enabled (plan includes feature)
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <XCircle size={12} /> E-signature not included. Upgrade to Professional or Enterprise to enable.
                </div>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange()} className="rounded-sm">Cancel</Button>
          <Button onClick={handleUpdate} disabled={saving} className="rounded-sm gap-2" data-testid="confirm-edit-org-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateGroupDialog({ open, onOpenChange, orgs, onCreated }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", holding_org_id: "" });

  const handleCreate = async () => {
    if (!form.name.trim() || !form.holding_org_id) { toast.error("Name and holding company are required"); return; }
    setSaving(true);
    try {
      await apiClient.post("/platform/groups", form);
      toast.success(`Group "${form.name}" created`);
      onOpenChange(false);
      setForm({ name: "", holding_org_id: "" });
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create group");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitBranch size={18} className="text-cyan-600" /> Create Group</DialogTitle>
          <DialogDescription>Link a holding company with its subsidiaries and branches.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label className="text-xs">Group Name *</Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Acme Holdings Group" className="rounded-sm" data-testid="create-group-name" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Holding Company *</Label>
            <Select value={form.holding_org_id || ""} onValueChange={v => setForm({ ...form, holding_org_id: v })}>
              <SelectTrigger className="rounded-sm" data-testid="create-group-holding"><SelectValue placeholder="Select holding company" /></SelectTrigger>
              <SelectContent>
                {orgs.filter(o => o.status === "active").map(o => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={handleCreate} disabled={saving} className="rounded-sm gap-2" data-testid="confirm-create-group-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Create Group
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const ROLE_OPTIONS = [
  { value: "superuser", label: "Superuser" },
  { value: "admin", label: "Admin" },
  { value: "legal", label: "Legal" },
  { value: "contract_creator", label: "Contract Creator" },
  { value: "contract_owner", label: "Contract Owner" },
  { value: "finance", label: "Finance" },
  { value: "executive", label: "Executive" },
  { value: "external", label: "External" },
];

const ROLE_COLORS = {
  superuser: "bg-red-100 text-red-700",
  admin: "bg-orange-100 text-orange-700",
  legal: "bg-blue-100 text-blue-700",
  contract_creator: "bg-emerald-100 text-emerald-700",
  contract_owner: "bg-slate-100 text-slate-600",
  finance: "bg-amber-100 text-amber-700",
  executive: "bg-purple-100 text-purple-700",
  external: "bg-violet-100 text-violet-700",
};

function MembersTable({ members, filterOrgId, onEditMembership, onRemoveMembership }) {
  const filtered = useMemo(() =>
    filterOrgId === "all"
      ? members
      : members.filter(m => m.org_memberships.some(om => om.org_id === filterOrgId)),
    [members, filterOrgId]
  );

  if (filtered.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <Users size={32} className="mx-auto mb-2 opacity-40" />
        <p className="text-sm">No members found</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="members-table">
        <thead>
          <tr className="border-b text-left text-xs font-mono uppercase tracking-wider text-slate-400">
            <th className="pb-3 pr-4">User</th>
            <th className="pb-3 pr-4">Status</th>
            <th className="pb-3 pr-4">Organizations & Roles</th>
            <th className="pb-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((user) => {
            const displayMemberships = filterOrgId === "all"
              ? user.org_memberships
              : user.org_memberships.filter(m => m.org_id === filterOrgId);
            return (
              <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`member-row-${user.id}`}>
                <td className="py-3 pr-4">
                  <div>
                    <p className="font-medium text-slate-900">{user.name || "Unnamed"}</p>
                    <p className="text-xs text-slate-400">{user.email}</p>
                  </div>
                </td>
                <td className="py-3 pr-4">
                  <Badge className={`rounded-sm text-[10px] ${user.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {user.is_active ? "Active" : "Inactive"}
                  </Badge>
                </td>
                <td className="py-3 pr-4">
                  <div className="space-y-1.5">
                    {displayMemberships.map((m) => (
                      <div key={m.org_id} className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-slate-600 font-medium min-w-[100px]">{m.org_name}</span>
                        {m.roles.map(r => (
                          <Badge key={r} className={`rounded-sm text-[9px] px-1.5 ${ROLE_COLORS[r] || "bg-slate-100"}`}>{r}</Badge>
                        ))}
                      </div>
                    ))}
                  </div>
                </td>
                <td className="py-3">
                  <div className="flex gap-1">
                    {displayMemberships.map((m) => (
                      <React.Fragment key={m.org_id}>
                        <Button variant="outline" size="sm" className="h-7 rounded-sm gap-1 text-xs" onClick={() => onEditMembership(user, m.org_id)} data-testid={`edit-membership-${user.id}-${m.org_id}`}>
                          <Edit size={10} />
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 rounded-sm gap-1 text-xs text-red-500 border-red-200 hover:bg-red-50" onClick={() => onRemoveMembership(user.id, m.org_id)} data-testid={`remove-membership-${user.id}-${m.org_id}`}>
                          <Trash2 size={10} />
                        </Button>
                      </React.Fragment>
                    ))}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function EditMembershipDialog({ membership, onOpenChange, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    if (membership?.user) {
      const m = membership.user.org_memberships.find(om => om.org_id === membership.org_id);
      setRoles(m?.roles || []);
    }
  }, [membership]);

  const toggleRole = (role) => {
    setRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  };

  const handleSave = async () => {
    if (roles.length === 0) { toast.error("At least one role is required"); return; }
    setSaving(true);
    try {
      await apiClient.put(`/platform/users/${membership.user.id}/memberships/${membership.org_id}`, { roles });
      toast.success("Roles updated");
      onOpenChange();
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  const orgName = membership?.user?.org_memberships?.find(m => m.org_id === membership?.org_id)?.org_name || "";

  return (
    <Dialog open={!!membership} onOpenChange={() => onOpenChange()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Edit size={18} className="text-blue-600" /> Edit Membership</DialogTitle>
          <DialogDescription>
            Update roles for <strong>{membership?.user?.name || membership?.user?.email}</strong> in <strong>{orgName}</strong>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2" data-testid="edit-membership-form">
          <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Assigned Roles</Label>
          <div className="grid grid-cols-2 gap-2">
            {ROLE_OPTIONS.map((r) => (
              <label key={r.value} className={`flex items-center gap-2 p-2 border rounded-sm cursor-pointer transition-colors text-sm ${
                roles.includes(r.value) ? "border-cyan-300 bg-cyan-50" : "border-slate-200 hover:bg-slate-50"
              }`}>
                <input type="checkbox" checked={roles.includes(r.value)} onChange={() => toggleRole(r.value)} className="rounded" />
                <span>{r.label}</span>
              </label>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange()} className="rounded-sm">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-sm gap-2" data-testid="confirm-edit-membership-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Roles
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddMembershipDialog({ open, onOpenChange, members, orgs, onAdded }) {
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState("");
  const [orgId, setOrgId] = useState("");
  const [roles, setRoles] = useState(["contract_creator"]);

  const toggleRole = (role) => {
    setRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  };

  const handleAdd = async () => {
    if (!userId || !orgId) { toast.error("Select a user and organization"); return; }
    if (roles.length === 0) { toast.error("At least one role is required"); return; }
    setSaving(true);
    try {
      await apiClient.post(`/platform/users/${userId}/memberships`, { org_id: orgId, roles, is_primary: false });
      toast.success("Membership added");
      onOpenChange(false);
      setUserId(""); setOrgId(""); setRoles(["contract_creator"]);
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add membership");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus size={18} className="text-emerald-600" /> Add Membership</DialogTitle>
          <DialogDescription>Add an existing platform user to an organization with specific roles.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2" data-testid="add-membership-form">
          <div className="space-y-1">
            <Label className="text-xs">User</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="rounded-sm" data-testid="add-membership-user"><SelectValue placeholder="Select user" /></SelectTrigger>
              <SelectContent>
                {members.filter(m => m.is_active).map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name || m.email} ({m.email})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Organization</Label>
            <Select value={orgId} onValueChange={setOrgId}>
              <SelectTrigger className="rounded-sm" data-testid="add-membership-org"><SelectValue placeholder="Select organization" /></SelectTrigger>
              <SelectContent>
                {orgs.filter(o => o.status === "active").map(o => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Roles</Label>
            <div className="grid grid-cols-2 gap-2">
              {ROLE_OPTIONS.map((r) => (
                <label key={r.value} className={`flex items-center gap-2 p-2 border rounded-sm cursor-pointer transition-colors text-sm ${
                  roles.includes(r.value) ? "border-cyan-300 bg-cyan-50" : "border-slate-200 hover:bg-slate-50"
                }`}>
                  <input type="checkbox" checked={roles.includes(r.value)} onChange={() => toggleRole(r.value)} className="rounded" />
                  <span>{r.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={handleAdd} disabled={saving} className="rounded-sm gap-2" data-testid="confirm-add-membership-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Add to Organization
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
